import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Landmark, Pencil, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { BankCard3D } from "@/components/finance/BankCard3D";
import { Verdict } from "@/components/common/Verdict";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { BANKS, OrgLogo } from "@/lib/banks";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const KEY = ["payment-means"];
const usePM = () => useQuery({ queryKey: KEY, queryFn: () => api.get("/finance/payment-means").then((r) => r.data) });
const F = ({ label, children }) => <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>;

const BankSelect = ({ v, setV }) => (
  <Select value={v.bank_slug || "other"} onValueChange={(s) => setV({ ...v, bank_slug: s === "other" ? null : s, bank: s === "other" ? "" : BANKS.find((b) => b.slug === s).name })}>
    <SelectTrigger data-testid="pm-bank-select"><SelectValue /></SelectTrigger>
    <SelectContent>{BANKS.map((b) => <SelectItem key={b.slug} value={b.slug}><span className="flex items-center gap-2"><OrgLogo slug={b.slug} name={b.name} className="h-5 w-5" />{b.name}</span></SelectItem>)}
      <SelectItem value="other">Autre banque</SelectItem></SelectContent>
  </Select>
);

const fromTemplate = (t, v) => ({ ...v, template: t.key, fee_year: t.fee_year, payment_limit: t.payment_limit, withdrawal_limit: t.withdrawal_limit, guarantees: t.guarantees.join("\n") });

const CardDialog = ({ card, templates, onClose }) => {
  const qc = useQueryClient();
  const tpl = (k) => ({ key: k, ...templates[k] });
  const [v, setV] = useState(card ? { ...card, guarantees: card.guarantees.join("\n") }
    : fromTemplate(tpl("visa_classic"), { bank: "BNP Paribas", bank_slug: "bnp", holder: "", last4: "", expiry: "", status: "active" }));
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...v, fee_year: Number(v.fee_year), payment_limit: Number(v.payment_limit), withdrawal_limit: Number(v.withdrawal_limit), guarantees: v.guarantees.split("\n").filter((g) => g.trim()) };
    ["id", "label", "network", "tier", "is_demo", "created_at", "color"].forEach((k) => delete body[k]);
    try {
      await (card ? api.put(`/finance/payment-means/cards/${card.id}`, body) : api.post("/finance/payment-means/cards", body));
      qc.invalidateQueries({ queryKey: KEY });
      toast.success(card ? "Carte mise à jour" : "Carte ajoutée");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pm-card-dialog">
        <DialogHeader><DialogTitle className="font-heading">{card ? "Modifier la carte" : "Ajouter une carte"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <F label="Modèle de carte"><Select value={v.template} onValueChange={(k) => setV(fromTemplate(tpl(k), v))}><SelectTrigger data-testid="pm-template-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(templates).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}</SelectContent></Select></F>
          <F label="Banque"><BankSelect v={v} setV={setV} /></F>
          {!v.bank_slug && <F label="Nom de la banque"><Input value={v.bank} onChange={set("bank")} required minLength={2} data-testid="pm-bank-input" /></F>}
          <F label="Titulaire"><Input value={v.holder} onChange={set("holder")} required minLength={2} data-testid="pm-holder-input" /></F>
          <F label="4 derniers chiffres"><Input value={v.last4} onChange={set("last4")} required pattern="\d{4}" maxLength={4} inputMode="numeric" placeholder="1234" data-testid="pm-last4-input" /></F>
          <F label="Expiration (MM/AA)"><Input value={v.expiry} onChange={set("expiry")} required pattern="(0[1-9]|1[0-2])/\d{2}" maxLength={5} placeholder="09/28" data-testid="pm-expiry-input" /></F>
          <F label="Statut"><Select value={v.status} onValueChange={set("status")}><SelectTrigger data-testid="pm-status-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="blocked">Bloquée</SelectItem><SelectItem value="expired">Expirée</SelectItem></SelectContent></Select></F>
          <F label="Cotisation annuelle €"><Input type="number" min="0" step="0.01" value={v.fee_year} onChange={set("fee_year")} data-testid="pm-fee-input" /></F>
          <F label="Plafond de paiement (30 j) €"><Input type="number" min="0" value={v.payment_limit} onChange={set("payment_limit")} data-testid="pm-payment-limit-input" /></F>
          <F label="Plafond de retrait (7 j) €"><Input type="number" min="0" value={v.withdrawal_limit} onChange={set("withdrawal_limit")} data-testid="pm-withdrawal-limit-input" /></F>
          <div className="sm:col-span-2"><F label="Garanties incluses (une par ligne)"><Textarea rows={5} value={v.guarantees} onChange={set("guarantees")} data-testid="pm-guarantees-input" /></F>
            <p className="mt-1 text-[11px] text-muted-foreground">Valeurs pré-remplies indicatives selon le modèle : vérifiez-les sur la notice de votre banque. Ne saisissez jamais le numéro complet ni le cryptogramme.</p></div>
          <Button type="submit" className="rounded-full sm:col-span-2" data-testid="pm-card-submit">{card ? "Enregistrer" : "Ajouter la carte"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CardItem = ({ c, onEdit }) => {
  const qc = useQueryClient();
  const del = async () => { try { await api.delete(`/finance/payment-means/cards/${c.id}`); qc.invalidateQueries({ queryKey: KEY }); toast.success("Carte supprimée"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="space-y-3" data-testid={`pm-card-${c.id}`}>
      <BankCard3D c={c} />
      <Verdict b={c.benchmark} testid={`pm-card-verdict-${c.id}`} className="ml-1" />
      <details className="max-w-sm rounded-xl border border-border bg-card px-4 py-2 text-sm">
        <summary className="flex cursor-pointer items-center gap-2 font-semibold" data-testid={`pm-card-guarantees-${c.id}`}><ShieldCheck className="h-4 w-4 text-success" />{c.guarantees.length} garanties incluses</summary>
        <ul className="mt-2 space-y-1.5 text-xs">{c.guarantees.map((g) => <li key={g} className="flex items-center gap-2"><OrgLogo slug={c.bank_slug} name={c.bank} className="h-5 w-5" />{g}</li>)}</ul>
        <div className="mt-3 flex gap-2 pb-1">
          <Button size="sm" variant="outline" className="rounded-full" onClick={onEdit} data-testid={`pm-card-edit-${c.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Modifier</Button>
          <ConfirmDelete title="Supprimer cette carte ?" text={`${c.label} •••• ${c.last4}`} testid={`pm-card-delete-${c.id}`} onConfirm={del} />
        </div>
      </details>
    </li>
  );
};

const ContractDialog = ({ onClose }) => {
  const qc = useQueryClient();
  const [v, setV] = useState({ bank: "BNP Paribas", bank_slug: "bnp", name: "", monthly_fee: "", services: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/payment-means/contracts", { ...v, monthly_fee: Number(v.monthly_fee), services: v.services.split("\n").filter((s) => s.trim()) });
      qc.invalidateQueries({ queryKey: KEY });
      toast.success("Contrat ajouté");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pm-contract-dialog">
        <DialogHeader><DialogTitle className="font-heading">Ajouter un contrat bancaire</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <F label="Banque"><BankSelect v={v} setV={setV} /></F>
          {!v.bank_slug && <Input value={v.bank} onChange={(e) => setV({ ...v, bank: e.target.value })} required minLength={2} placeholder="Nom de la banque" />}
          <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required minLength={2} placeholder="Nom de l'offre (ex. convention Esprit Libre)" data-testid="pm-contract-name-input" />
          <Input type="number" min="0" step="0.01" value={v.monthly_fee} onChange={(e) => setV({ ...v, monthly_fee: e.target.value })} required placeholder="Coût mensuel €" data-testid="pm-contract-fee-input" />
          <Textarea rows={4} value={v.services} onChange={(e) => setV({ ...v, services: e.target.value })} placeholder="Services inclus, un par ligne" data-testid="pm-contract-services-input" />
          <Button type="submit" className="w-full rounded-full" data-testid="pm-contract-submit">Ajouter</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Contracts = ({ list }) => {
  const qc = useQueryClient();
  const { money } = useFormat();
  const [open, setOpen] = useState(false);
  const del = async (id) => { try { await api.delete(`/finance/payment-means/contracts/${id}`); qc.invalidateQueries({ queryKey: KEY }); toast.success("Contrat supprimé"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section className="card-surface p-5" data-testid="pm-contracts">
      <div className="mb-3 flex flex-wrap items-center gap-3"><Landmark className="h-5 w-5" /><h2 className="flex-1 text-lg font-bold">Contrats bancaires</h2>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="pm-contract-add"><Plus className="mr-1 h-4 w-4" />Ajouter</Button></div>
      <ul className="grid gap-3 md:grid-cols-2">{list.map((c) => (
        <li key={c.id} className="rounded-xl border border-border p-4 text-sm" data-testid={`pm-contract-${c.id}`}>
          <div className="flex items-center gap-3"><OrgLogo slug={c.bank_slug} name={c.bank} className="h-10 w-10" />
            <div className="min-w-0 flex-1"><div className="truncate font-semibold">{c.name}</div><div className="truncate text-xs text-muted-foreground">{c.bank}</div></div>
            <div className="text-right"><div className="num font-bold">{money(c.monthly_fee, { maximumFractionDigits: 2 })}</div><div className="text-[11px] text-muted-foreground">/mois</div></div>
            <ConfirmDelete title="Supprimer ce contrat ?" text={c.name} testid={`pm-contract-delete-${c.id}`} onConfirm={() => del(c.id)} /></div>
          {c.services.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{c.services.map((s) => <span key={s} className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px]">{s}</span>)}</div>}
          <Verdict b={c.benchmark} testid={`pm-contract-verdict-${c.id}`} className="mt-3" />
        </li>))}</ul>
      {!list.length && <p className="text-sm text-muted-foreground">Ajoutez votre convention de compte pour suivre son coût.</p>}
      {open && <ContractDialog onClose={() => setOpen(false)} />}
    </section>
  );
};

export const PaymentMeans = () => {
  const { money } = useFormat();
  const { data } = usePM();
  const [edit, setEdit] = useState(null);
  if (!data) return <div className="h-60 animate-pulse rounded-2xl bg-muted" />;
  return (
    <div className="space-y-8" data-testid="payment-means">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <CreditCard className="h-5 w-5" /><h2 className="flex-1 text-lg font-bold">Mes cartes bancaires<span className="block text-sm font-normal text-muted-foreground">{data.active_count} active(s) · frais bancaires {money(data.fees_year)}/an</span></h2>
          <Button size="sm" className="rounded-full" onClick={() => setEdit({})} data-testid="pm-card-add"><Plus className="mr-1 h-4 w-4" />Ajouter une carte</Button>
        </div>
        <ul className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">{data.cards.map((c) => <CardItem key={c.id} c={c} onEdit={() => setEdit(c)} />)}</ul>
        {!data.cards.length && <p className="text-sm text-muted-foreground">Aucune carte enregistrée.</p>}
      </section>
      <Contracts list={data.contracts} />
      {edit && <CardDialog card={edit.id ? edit : null} templates={data.templates} onClose={() => setEdit(null)} />}
    </div>
  );
};
